import React from 'react';
import Modal from '../../../shared/components/modals/Modal';
import './TrendsAnalyticsHelpModal.css';

const HELP_BY_SECTION = {
  scorecard: {
    title: 'Performance Scorecard',
    lead: (
      <>
        Cards show the <strong>latest Compare bucket</strong>. Deltas compare that bucket to the
        start of the selected range. Sparklines run <strong>oldest → newest</strong>.
      </>
    ),
    blocks: [
      {
        heading: 'Compliance %',
        items: [
          <>
            <strong>Calibration, PPM, Regulatory</strong> — due-date snapshots for that period,
            not a running average of the range.
          </>,
          <>
            A device is compliant in a bucket if it was not overdue at that period’s as-of date.
          </>,
        ],
      },
      {
        heading: 'Health & issues',
        items: [
          <>
            <strong>Overall Device Health</strong> — mean of Calibration, PPM, and Regulatory %
            for categories that have applicable devices in that bucket (empty categories are
            omitted, not treated as 0%).
          </>,
          <>
            <strong>Critical Issues</strong> — active critical complaints only (open, acknowledged,
            or in progress). Lower is better.
          </>,
        ],
      },
      {
        heading: 'Resolution rate',
        items: [
          <>
            Percentage of complaints <strong>created in the period</strong> that were{' '}
            <strong>closed by the end of that period</strong> (so older buckets do not rise later
            when tickets close). Hover the card for closed / created counts.
          </>,
        ],
      },
      {
        heading: 'Budget utilization',
        items: [
          <>
            Calendar-year YTD spend ÷ the <strong>current</strong> approved org budget.
            Historical budget amounts are not stored, so prior-year % uses today’s budget as the
            reference.
          </>,
          <>
            Within-year Compare views show the <strong>YTD level only</strong>. Trend arrows for
            utilization appear on the <strong>Year</strong> compare (same calendar YTD dates).
          </>,
          <>
            Sparkline bars show <strong>period spend</strong> (not utilization %) oldest → newest.
          </>,
        ],
      },
    ],
  },
  location: {
    title: 'Location Performance',
    lead: (
      <>
        Locations ranked by <strong>attention</strong> for the selected Compare period (risk first,
        then largest decline). Click a row to open that location’s deep-dive.
      </>
    ),
    blocks: [
      {
        heading: 'Metrics',
        items: [
          <>
            <strong>Calibration, PPM, Regulatory, Health Score</strong> — due-date / health
            snapshots for that location in each bucket.
          </>,
          <>
            <strong>Critical Issues</strong> — active critical complaints (open, acknowledged, or
            in progress). Lower is better.
          </>,
        ],
      },
      {
        heading: 'Risk level',
        items: [
          <>
            Same tiers as Department Trends: <strong>Critical</strong> &lt; 60%,{' '}
            <strong>Warning</strong> &lt; 75%, otherwise <strong>Good</strong> — based on the
            weakest present compliance category or health score.
          </>,
        ],
      },
    ],
  },
  siteMetrics: {
    title: 'Site metrics',
    lead: (
      <>
        Location deep-dive overview for the selected site over a fixed rolling{' '}
        <strong>8 weeks</strong> (not tied to the Scorecard Compare control).
      </>
    ),
    blocks: [
      {
        heading: 'What is shown',
        items: [
          <>
            <strong>Calibration, PPM, Regulatory</strong> — due-date compliance snapshots for this
            location.
          </>,
          <>
            <strong>Overall Health Score</strong> — mean of Cal / PPM / Reg for categories that
            have applicable devices (empty categories are omitted, not counted as 0%).
          </>,
          <>
            <strong>Critical Issues</strong> — count of critical complaints that were{' '}
            <strong>still open</strong> at that week’s as-of date (stock), not how many were
            created that week. A bar of 2 then 2 then 2 means two stayed open, not two new each week.
          </>,
        ],
      },
      {
        heading: 'Change & forecast',
        items: [
          <>
            Change compares the latest week to 8 weeks ago. For compliance %, forecast is a short
            linear projection if that pace continues. For critical issues we do{' '}
            <strong>not</strong> project a future volume — the detail is current open count vs 8
            weeks ago.
          </>,
        ],
      },
    ],
  },
  deepDiveDepartments: {
    title: 'Departments (deep-dive)',
    lead: (
      <>
        All departments at this location for the selected Metric (shared with Across locations).
        Rows start <strong>worst first</strong>; click column headers to change the sort.
      </>
    ),
    blocks: [
      {
        heading: 'Shared Metric dropdown',
        items: [
          <>
            Pick Calibration, PPM, Regulatory, Health Score, or Open Critical Issues. The same
            choice drives this table and Across locations so the numbers stay aligned.
          </>,
          <>
            Health Score here is the same formula as Site metrics: average of present Cal / PPM /
            Reg categories for that department.
          </>,
          <>
            For Open Critical Issues, each sparkline bar is how many criticals were{' '}
            <strong>open that week</strong>, not new tickets opened that week.
          </>,
        ],
      },
      {
        heading: 'Sorting',
        items: [
          <>
            Default is worst first for the selected metric (steepest decline, or highest open
            criticals). Click Department, Current, or Change to re-sort; click again to reverse.
          </>,
        ],
      },
    ],
  },
  acrossLocations: {
    title: 'Across locations',
    lead: (
      <>
        Peer comparison for the selected Metric across all sites (rolling 8 weeks). This site is
        highlighted. Rows start <strong>worst first</strong>; click headers to re-sort.
      </>
    ),
    blocks: [
      {
        heading: 'How to use it',
        items: [
          <>
            Change Metric here or in Departments — both sections stay in sync.
          </>,
          <>
            Use this to answer “is this site uniquely bad on this metric?” then use Departments to
            see which units drive it.
          </>,
        ],
      },
    ],
  },
  department: {
    title: 'Department Trends',
    lead: (
      <>
        Org-wide <strong>top 5 departments needing attention</strong> for the selected Compare
        window. Rows are ranked across all locations — not limited to one site.
      </>
    ),
    blocks: [
      {
        heading: 'How ranking works',
        items: [
          <>
            Sorted by <strong>risk first</strong> (Critical → Warning → Good), then by the{' '}
            <strong>largest decline</strong> in calibration / PPM / regulatory / health over the
            Compare window, then by the weakest current category.
          </>,
        ],
      },
      {
        heading: 'Trend assessment',
        items: [
          <>
            Overall badge is <strong>Improving</strong>, <strong>Declining</strong>, or{' '}
            <strong>Stable</strong> from how calibration, PPM, and regulatory move over the Compare
            window. If those three disagree, the line under the badge names each one (for example,
            “Stable: PPM, Reg · Declining: Cal”).
          </>,
        ],
      },
      {
        heading: 'Risk level',
        items: [
          <>
            Based on the lowest compliance category or health score in the current bucket:{' '}
            <strong>Critical</strong> &lt; 60%, <strong>Warning</strong> &lt; 75%, otherwise Good.
          </>,
        ],
      },
    ],
  },
  financial: {
    title: 'Financial Trends',
    lead: (
      <>
        Spend and utilization for the <strong>selected Compare</strong> window, with deltas vs the
        start of the range.
      </>
    ),
    blocks: [
      {
        heading: 'Period CAPEX / OPEX',
        items: [
          <>
            <strong>CAPEX</strong> — device / capital spend attributed to the Compare window.
          </>,
          <>
            <strong>OPEX (Maintenance)</strong> — maintenance / contract spend in the Compare
            window. For trend coloring, lower opex movement is treated as better.
          </>,
        ],
      },
      {
        heading: 'Budget utilization',
        items: [
          <>
            Calendar-year YTD spend ÷ the <strong>current</strong> approved org budget (same
            reference when comparing a prior year). Trend delta only on Year compare; other
            windows show YTD level without an improving/declining arrow.
          </>,
        ],
      },
    ],
  },
};

export default function TrendsAnalyticsHelpModal({ section, onClose }) {
  const content = section ? HELP_BY_SECTION[section] : null;
  if (!content) return null;

  return (
    <Modal isOpen={Boolean(section)} onClose={onClose} title={content.title} size="large">
      <div className="scorecard-help">
        <p className="scorecard-help__lead">{content.lead}</p>
        {content.blocks.map((block) => (
          <section key={block.heading}>
            <h3>{block.heading}</h3>
            <ul className="scorecard-help__list">
              {block.items.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Modal>
  );
}
